import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, UserPlus } from 'lucide-react';
import { UnlockPlansModal, useBodyScrollLock } from '@/components/UnlockPlansModal';
import { getLocalizedPath } from '@/utils/localizedRoutes';

export const goAuth = (navigate, path, lang, returnTo) => {
  localStorage.setItem('postRegisterRedirect', returnTo);
  sessionStorage.setItem('redirectAfterLogin', returnTo);
  navigate(getLocalizedPath(path, lang));
};

// Items 0-1 free for everyone, item 2 free with an account, rest need a plan.
export const gateFor = (index, user, isSubscribed) => {
  if (isSubscribed || index < 2) return 'open';
  if (index === 2) return user ? 'open' : 'register';
  return 'plans';
};

const T = {
  title: { sv: 'Lås upp gratis', en: 'Unlock for free', ar: 'افتحه مجاناً', tr: 'Ücretsiz aç', fa: 'رایگان باز کنید', so: 'Si bilaash ah u fur', pl: 'Odblokuj za darmo', es: 'Desbloquéalo gratis', de: 'Kostenlos freischalten' },
  desc: { sv: 'Registrera dig på DriveMaster och fortsätt lära dig direkt.', en: 'Sign up to DriveMaster and keep learning right away.', ar: 'سجّل في DriveMaster وتابع التعلّم فوراً.', tr: 'DriveMaster\'a kaydolun ve hemen öğrenmeye devam edin.', fa: 'در DriveMaster ثبت‌نام کنید و فوراً به یادگیری ادامه دهید.', so: 'Iska diiwaangeli DriveMaster oo isla markiiba sii wad barashada.', pl: 'Zarejestruj się w DriveMaster i od razu ucz się dalej.', es: 'Regístrate en DriveMaster y sigue aprendiendo enseguida.', de: 'Registriere dich bei DriveMaster und lerne sofort weiter.' },
  cta: { sv: 'Registrera dig', en: 'Sign up', ar: 'إنشاء حساب', tr: 'Kaydol', fa: 'ثبت‌نام', so: 'Is diiwaangeli', pl: 'Zarejestruj się', es: 'Registrarse', de: 'Registrieren' },
  login: { sv: 'Jag har redan ett konto', en: 'I already have an account', ar: 'لدي حساب بالفعل', tr: 'Zaten hesabım var', fa: 'قبلاً حساب دارم', so: 'Horey ayaan akoon u leeyahay', pl: 'Mam już konto', es: 'Ya tengo una cuenta', de: 'Ich habe bereits ein Konto' },
};

const RegisterFreeModal = ({ onClose, lang }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = (k) => T[k][lang] || T[k].en;
  useBodyScrollLock(true);
  const go = (path) => { onClose(); goAuth(navigate, path, lang, pathname); };
  return (
    <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4" onClick={onClose} data-testid="register-free-modal" dir={['ar', 'fa'].includes(lang) ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-sm w-full p-7 text-center relative shadow-2xl animate-in fade-in zoom-in duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 end-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="register-free-close" aria-label="close">
          <X className="w-5 h-5 text-slate-500" />
        </button>
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
          <UserPlus className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 mb-2">{t('title')}</h3>
        <p className="text-slate-600 dark:text-slate-400 mb-6">{t('desc')}</p>
        <button onClick={() => go('/registrera')} data-testid="register-free-cta"
          className="w-full h-13 py-3.5 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold text-lg shadow-lg hover:brightness-105 active:scale-[0.98] transition-[filter,transform]">
          {t('cta')}
        </button>
        <button onClick={() => go('/logga-in')} data-testid="register-free-login" className="mt-4 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors">
          {t('login')}
        </button>
      </div>
    </div>
  );
};

// mode: null | 'register' | 'plans'
export const ContentGateModal = ({ mode, onClose, lang, license = null }) => {
  if (mode === 'register') return <RegisterFreeModal onClose={onClose} lang={lang} />;
  return <UnlockPlansModal open={mode === 'plans'} onClose={onClose} license={license} lang={lang} />;
};
